/**
 * Bridge between an external-wallet SDK (Privy / Para / DogeOS) and @web3-react/core.
 *
 * The SDK owns the actual wallet connection; we mirror it into the singleton
 * `EIP1193Connector` so `useWeb3React` — and therefore `useWallet` and every
 * downstream consumer — sees a normal active connection. This module is the single
 * choke point for that mirroring, so both `useWallet.activate()` (imperative, on
 * user click) and `<WalletBridge/>` (reactive, on session restore / provider churn)
 * funnel through the same deduped, rollback-safe path.
 *
 * State is module-level (not React state) to match the singleton connector and to
 * make the dedupe survive re-renders. There is no chain-switch coupling here: chain
 * changes are handled by the connector's own `chainChanged` listener attached in
 * `adopt()`.
 */
import { eip6963Connector } from "../connectors/eip6963";
import type { EIP1193Like } from "./types";

let bridgedKey: string | null = null;
let bridgedProvider: EIP1193Like | null = null;

const parseChainId = (raw: string | number): number =>
  typeof raw === "string" ? parseInt(raw, 16) : Number(raw);

/**
 * Mirror an SDK provider into web3-react. Idempotent: a repeat call with the same
 * (account, chainId, provider) is a no-op, so `useWallet.activate` and the reactive
 * `<WalletBridge/>` effect can't double-activate. On failure, rolls back to a clean
 * disconnected state so the next attempt starts fresh.
 */
export async function bridgeProvider(
  provider: EIP1193Like,
  account: string,
  chainId?: number
): Promise<void> {
  let cid = chainId;
  if (cid == null) {
    const raw = await provider.request({ method: "eth_chainId" });
    cid = parseChainId(raw);
  }

  const key = `${account.toLowerCase()}:${cid}`;
  if (key === bridgedKey && provider === bridgedProvider) {
    return; // already bridged to this exact identity
  }

  try {
    await eip6963Connector.adopt(provider, { accounts: [account], chainId: cid });
    bridgedKey = key;
    bridgedProvider = provider;
  } catch (error) {
    // Roll back: never leave a half-bridged state (provider set but web3-react
    // inactive), or the next connect attempt inherits stale wiring.
    bridgedKey = null;
    bridgedProvider = null;
    eip6963Connector.setProvider(undefined as unknown as EIP1193Like);
    await eip6963Connector.resetState();
    throw error;
  }
}

/** Tear down the web3-react side of the bridge (SDK logout is the caller's job). */
export async function unbridgeProvider(): Promise<void> {
  bridgedKey = null;
  bridgedProvider = null;
  try {
    await eip6963Connector.deactivate();
  } catch {
    // best-effort; resetState still clears web3-react state
    await eip6963Connector.resetState();
  }
}

/** True when an SDK provider is currently mirrored into web3-react. */
export const isBridged = (): boolean => bridgedKey !== null;
